import { useEffect, useState, useRef } from "react";
import { NavLink } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useIsMobile } from "@/hooks/useIsMobile";
import CurrencySelect from "@/components/CurrencySelect";
import ThemeToggle from "@/components/ThemeToggle";
import RecentActivity from "@/components/RecentActivity";
import { Sparkles, Settings2, User, Bell } from "lucide-react";

// Patch 55 Part B1/J — credits + currency + theme merged into ONE top-right cluster.
// Desktop: an inline row. Narrow viewports: a single button opening a dropdown (never three cramped icons).
export default function TopRightCluster() {
  const { org } = useAuth();
  const isMobile = useIsMobile(768);
  const [bal, setBal] = useState(null);
  const [low, setLow] = useState(false);
  const [openCredits, setOpenCredits] = useState(false);
  const [openMenu, setOpenMenu] = useState(false);
  const [openNotif, setOpenNotif] = useState(false);
  const ref = useRef(null);

  const refresh = () => {
    if (org?.id) api.get(`/orgs/${org.id}/balance`)
      .then(r => { setBal(r.data.balance_credits); setLow(!!r.data.is_low); }).catch(() => {});
  };
  useEffect(() => {
    refresh();
    const f = () => refresh();
    window.addEventListener("focus", f);
    return () => window.removeEventListener("focus", f);
    // eslint-disable-next-line
  }, [org]);
  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) { setOpenCredits(false); setOpenMenu(false); setOpenNotif(false); } };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const creditsBtn = (
    <div style={{ position: "relative" }}>
      <button className={`k-badge ${low ? "k-badge-sandbox" : "k-badge-accent"}`} data-testid="tr-credits"
        aria-haspopup="true" aria-expanded={openCredits} style={{ cursor: "pointer" }}
        onClick={() => setOpenCredits(o => !o)}><Sparkles size={13} /> {bal != null ? Number(bal).toLocaleString("en-GB") : "…"} credits</button>
      {openCredits && (
        <div className="k-container" data-testid="tr-credits-popover" role="menu"
          style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", width: 220, padding: 14, zIndex: 80 }}>
          <div className="k-heading" style={{ fontSize: 14, fontWeight: 700 }}>{bal != null ? Number(bal).toLocaleString("en-GB") : "…"} credits</div>
          {low && <p className="k-muted" style={{ fontSize: 11, marginTop: 4 }}>Running low — top up whenever it suits you.</p>}
          <NavLink to="/billing" className="k-btn k-btn-primary" data-testid="tr-topup"
            style={{ width: "100%", marginTop: 10, fontSize: 13 }} onClick={() => setOpenCredits(false)}>Top up</NavLink>
          <NavLink to="/billing" className="k-link" data-testid="tr-view-billing"
            style={{ display: "inline-block", marginTop: 10, fontSize: 12 }} onClick={() => setOpenCredits(false)}>View full billing →</NavLink>
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <div ref={ref} data-testid="top-right-cluster"
        style={{ display: "flex", justifyContent: "flex-end", marginBottom: 8, position: "relative" }}>
        <button className="k-btn k-btn-ghost" data-testid="tr-menu-btn" aria-haspopup="true" aria-expanded={openMenu}
          aria-label="Account, currency and theme" style={{ padding: "8px 11px", minHeight: 0 }}
          onClick={() => setOpenMenu(o => !o)}><Settings2 size={17} /></button>
        {openMenu && (
          <div className="k-container" data-testid="tr-menu"
            style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", width: 240, padding: 14, zIndex: 80,
              display: "flex", flexDirection: "column", gap: 12 }}>
            {creditsBtn}
            <CurrencySelect compact />
            <ThemeToggle />
            <NavLink to="/profile" data-testid="tr-profile-m" className="k-btn k-btn-ghost" style={{ justifyContent: "flex-start" }} onClick={() => setOpenMenu(false)}><User size={15} /> Profile</NavLink>
          </div>
        )}
      </div>
    );
  }
  return (
    <div ref={ref} data-testid="top-right-cluster"
      style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", gap: 12, marginBottom: 8 }}>
      {creditsBtn}
      <CurrencySelect compact />
      <ThemeToggle compact />
      <NavLink to="/profile" data-testid="tr-profile" className="k-btn k-btn-ghost" aria-label="Profile" style={{ padding: "7px 9px", minHeight: 0 }}><User size={16} /></NavLink>
      <div style={{ position: "relative" }}>
        <button className="k-btn k-btn-ghost" data-testid="tr-notifications" aria-label="Notifications" aria-haspopup="true" aria-expanded={openNotif}
          onClick={() => setOpenNotif(o => !o)} style={{ padding: "7px 9px", minHeight: 0 }}><Bell size={16} /></button>
        {openNotif && (
          <div className="k-container" data-testid="tr-notif-popover" role="menu"
            style={{ position: "absolute", right: 0, top: "calc(100% + 8px)", width: 300, padding: 12, zIndex: 80 }}>
            <div className="k-heading" style={{ fontSize: 13, fontWeight: 700, marginBottom: 8 }}>Recent activity</div>
            <RecentActivity />
          </div>
        )}
      </div>
    </div>
  );
}
